import { useMemo } from "react";
import { useLoadedData } from "../../../toolympus/hooks/useLoadedData"
import { FieldType, mergeSchema, Schema } from "../../../toolympus/hooks/useSchema";
import { exportRecordsToXlsxSheet } from "../../../toolympus/api/xlsx";
import { useTextFilter } from "../../../toolympus/components/schemed/Filtering/useTextFilter";
import { useTableComments } from "../../../toolympus/components/Comments";
import { useUserNames } from "../../../toolympus/hooks/useUserNames";
import { Product } from "./useProductsList";
import { useBrowserStoredValue } from "../../../toolympus/hooks/useBrowserStoredValue";
import { useProductSubmissionsFields } from "./useProductSubmissionsFields";

export interface EventRegistration {
  _id: number;
  email?: string;
  display_name?: string;
  registration_status?: string;
  registration_format?: string;
  info?: Record<string, any>;

  applied_discount_code?: string;
}

export const EventRegistrationSchema: Schema = {
  email: { label: "E-mail" },
  display_name: { label: "Name" },
  // registration_status: createSelectSchema([
  //   { value: "pending_confirmation", label: "Awaits confirmation" },
  //   { value: "pending_payment", label: "Awaits payment" },
  //   { value: "confirmed", label: "Confirmed" },
  //   { value: "rejected", label: "Rejected" },
  //   { value: "cancelled", label: "Cancelled" },
  // ], { label: "Status" }),
  
  is_paid: { label: "Paid?", type: FieldType.bool },
  invoice_paid_at: { label: "Paid at", type: FieldType.datetime },
  invoice_nbr: { label: "Invoice #" },
  invoice_amount: { label: "Invoice Amt.", type: FieldType.number },
  paid_amount: { label: "Paid Amt.", type: FieldType.number },
  created_at: { label: "Created", type: FieldType.datetime },
  status_log: { label: "Status log", type: FieldType.textlong },

  applied_discount_code: { label: "D/code"},
  applied_discount_amount: { label: "D/amount", type: FieldType.number },
}

export const useProductSubmissions = (kind: "admin" | "coorg", product: Product) => {
  const registrations = useLoadedData<EventRegistration[]>(`/api/product/admin/${product._id}/submissions`, [], !!product?._id);
  const view = useBrowserStoredValue("all", undefined, "_pform_submissions_view");

  const profileConfig = useProductSubmissionsFields(product);

  const userNames = useUserNames();
  const comments = useTableComments("/api/comment", `productsub_${product._id}`, {
    suppressLoad: !product?._id,
    getUserName: userNames.getUsername,
  });

  // const approve = useItemActionWithConfirmation<EventRegistration, EventRegistration>(
  //   (registration) => {
  //     return apiFetch<EventRegistration>(`/api/registration/${kind}/registration/${registration._id}/approve`, "post")
  //       .then(reg => {
  //         registrations.setData(x => x.map(r => r._id === reg._id ? { ...r, ...reg } : r));
  //         return reg;
  //       })
  //   }, {
  //     skipConfirmation: true,
  //   }
  // );

  // const reject = useItemActionWithConfirmation<EventRegistration, EventRegistration>(
  //   (registration) => {
  //     return apiFetch<EventRegistration>(`/api/registration/${kind}/registration/${registration._id}/reject`, "post")
  //       .then(reg => {
  //         registrations.setData(x => x.map(r => r._id === reg._id ? { ...r, ...reg } : r));
  //         return reg;
  //       })
  //   }, {
  //     skipConfirmation: true,
  //   }
  // );

  
  const filter = useTextFilter<EventRegistration>(r => `${r._id} ${r.email || ""} ${r.display_name || ""} ${r.applied_discount_code || ""}`);

  const registrationsWithProfiles = useMemo(() => {
    return registrations.data.map(r => ({
      ...(r.info || {}),
      ...r,
    }));
  }, [registrations]);

  const registrationsFiltered = useMemo(() => {
    const searchFiltered = filter.filterData(registrationsWithProfiles);
    if(view.value === "unprocessed") {
      return searchFiltered.filter(r => r.registration_status !== "paid" && r.registration_status !== "confirmed");
    } else if(view.value === "processed") {
      return searchFiltered.filter(r => r.registration_status === "paid" || r.registration_status === "confirmed");
    }
    return searchFiltered;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [registrationsWithProfiles, filter.filter])

  const schema = useMemo(() => mergeSchema(EventRegistrationSchema, profileConfig.schema), [profileConfig.schema]);

  const exportRecords = () => {
    exportRecordsToXlsxSheet({
      sheetTitle: "Submissions",
      schema,
      filename: `submissions ${product.title}.xlsx`,
      columns: [
        "registration_status",
        "email",
        "invoice_paid_at",
        "invoice_paid_at",
        "invoice_amount",
        "invoice_nbr",
        "applied_discount_code",
        "applied_discount_amount",
        ...profileConfig.simpleFields.filter(f => !!f).map(f => (f as any)[0]),
        "created_at",
        "status_log",
      ],
      prepare: r => ({
        ...r,
        registration_status: schema["registration_status"].valueDict[r.registration_status || ""],
        registration_format: schema["registration_format"].valueDict[r.registration_format || ""],
      })
    }, registrationsFiltered);
  }


  return {
    ...registrations,
    data: registrationsFiltered,
    schema,
    profileFields: profileConfig.simpleFields,
    filter,
    view,


    // approve,
    // reject,

    exportRecords,

    comments,

    // isActionRunning: approve.isRunning || reject.isRunning,
  }
}
